/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { Iconify } from 'src/components';

/** Was this reply any good? — asked on the reply itself.
 *
 *  This replaces a panel that sat above the composer and rated only the LATEST
 *  turn. Three things were wrong with it and they compounded:
 *
 *    * It judged one message while you were reading another. Scrolling back
 *      through a conversation, the reply you wanted to mark was never the one
 *      the panel was offering.
 *    * It cost roughly a third of the inbox: quoted text, two buttons, seven
 *      tag chips, a comment box and two lines of help, permanently, whether or
 *      not anyone was rating anything.
 *    * Tags and comment were inline, so the rare case — explaining a bad reply
 *      — set the size of the common case, which is one click and move on.
 *
 *  So: two small thumbs on the bubble. Good saves immediately and closes the
 *  matter. Bad saves immediately too — a verdict with no explanation still
 *  counts, and losing it because somebody shut a dialog would be worse than
 *  having no tags — and then opens the dialog, because the moment a person has
 *  just decided a reply was wrong is the only moment they can say why.
 */
export default function MessageRating({
    turnId, mine, tagOptions = [], replyText = '', onRate,
}) {
    const [open, setOpen] = useState(false);
    const [tags, setTags] = useState(mine?.tags || []);
    const [comment, setComment] = useState(mine?.comment || '');
    const [saving, setSaving] = useState(false);

    // The verdict is held HERE as well as on the message, so the thumb lights
    // the moment it is clicked.
    //
    // `mine` rides in on the message from the thread fetch, and a successful
    // rate never puts it back there: WhatsappAIModeControl.rate() stamps its
    // own state, not `msg.aiRating.mine` in the store, and the bubble reads the
    // store. So the prop still held the pre-click value and the thumb stayed
    // grey until a full page refresh - the rating had saved and the screen said
    // nothing had happened, which is the one thing a one-click control must
    // never do. The second click that follows toggles the rating back off.
    //
    // The server still wins. This re-seeds whenever the saved verdict changes,
    // so a refetch, a 409 correction or a colleague's rating all overwrite it.
    // A head start, not a second source of truth - and deliberately only the
    // verdict: tags and comment seed once on purpose, because re-seeding those
    // while the dialog is open would wipe a note somebody is mid-sentence on.
    const [verdict, setVerdict] = useState(mine?.verdict || null);
    const savedVerdict = mine?.verdict || null;

    useEffect(() => {
        setVerdict(savedVerdict);
    }, [savedVerdict]);

    // A media message's content is not a string. Quote only what can be quoted.
    const quoted = typeof replyText === 'string' ? replyText.trim() : '';

    const save = async (nextVerdict, nextTags, nextComment) => {
        const previous = verdict;
        setVerdict(nextVerdict);
        setSaving(true);
        try {
            // A verdict flip must not carry the previous one's failure tags:
            // "Good" with `wrong_information` on it is a row nobody can read.
            const cleanTags = nextVerdict === 'GOOD' ? [] : (nextTags || []);
            const res = await onRate({
                turn_id: turnId,
                verdict: nextVerdict,
                tags: cleanTags,
                comment: nextComment ?? comment,
            });
            // The caller already raises "that rating did not save". A lit thumb
            // sitting under that sentence is worse than the lag this replaces,
            // so an unsuccessful save puts the old verdict straight back.
            if (res && res.ok === false) setVerdict(previous);
            return res;
        } catch (err) {
            setVerdict(previous);
            throw err;
        } finally {
            setSaving(false);
        }
    };

    const onGood = () => {
        setTags([]);
        save(verdict === 'GOOD' ? null : 'GOOD', [], comment);
    };

    const onBad = () => {
        // Recorded first, dialog second. If the dialog is dismissed the verdict
        // still stands.
        save('BAD', tags, comment);
        setOpen(true);
    };

    const summary = mine
        ? [mine.verdict === 'GOOD' ? 'Marked good' : 'Marked bad',
            (mine.tags || []).join(', '), mine.comment].filter(Boolean).join(' — ')
        : 'Rate this reply';

    return (
        <>
            <Tooltip title={summary} placement="top">
                <Stack
                    direction="row"
                    spacing={0.25}
                    sx={{
                        // Quiet until wanted. An unrated reply shows two faint
                        // outlines; a rated one shows the thumb that was given.
                        opacity: verdict ? 1 : 0.35,
                        transition: 'opacity 120ms ease',
                        '&:hover': { opacity: 1 },
                    }}
                >
                    <Box
                        component="button"
                        type="button"
                        aria-label="Good reply"
                        disabled={saving}
                        onClick={onGood}
                        sx={{
                            border: 0, background: 'none', cursor: 'pointer', p: 0.25,
                            lineHeight: 0, borderRadius: 0.5,
                            color: verdict === 'GOOD' ? 'success.main' : 'text.disabled',
                            '&:hover': { color: 'success.main' },
                        }}
                    >
                        <Iconify
                            icon={verdict === 'GOOD' ? 'mdi:thumb-up' : 'mdi:thumb-up-outline'}
                            sx={{ width: 15, height: 15 }}
                        />
                    </Box>
                    <Box
                        component="button"
                        type="button"
                        aria-label="Bad reply"
                        disabled={saving}
                        onClick={onBad}
                        sx={{
                            border: 0, background: 'none', cursor: 'pointer', p: 0.25,
                            lineHeight: 0, borderRadius: 0.5,
                            color: verdict === 'BAD' ? 'error.main' : 'text.disabled',
                            '&:hover': { color: 'error.main' },
                        }}
                    >
                        <Iconify
                            icon={verdict === 'BAD' ? 'mdi:thumb-down' : 'mdi:thumb-down-outline'}
                            sx={{ width: 15, height: 15 }}
                        />
                    </Box>
                    {/* A rated reply carrying detail says so, so a colleague
                        scanning the thread can see which ones were explained
                        without hovering every bubble. */}
                    {!!(mine?.tags?.length || mine?.comment) && (
                        <Iconify
                            icon="mdi:note-text-outline"
                            sx={{ width: 13, height: 13, color: 'text.disabled', ml: 0.25 }}
                        />
                    )}
                </Stack>
            </Tooltip>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>
                    What was wrong with it?
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        The thumbs-down is already saved — this is only detail.
                        Tags add up across replies; the note is read by a person.
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {/* WHICH reply. A colleague opens this dialog after
                        clicking a bubble four messages up, and the dialog
                        covers the conversation — so without this they are
                        describing a reply they can no longer see. */}
                    {!!quoted && (
                        <Box
                            sx={{
                                mb: 2, p: 1.25, borderRadius: 1,
                                bgcolor: 'background.neutral',
                                borderLeft: (t) => `3px solid ${t.palette.error.main}`,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    display: '-webkit-box', WebkitLineClamp: 4,
                                    WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                }}
                            >
                                {quoted}
                            </Typography>
                        </Box>
                    )}

                    {tagOptions.length > 0 ? (
                        <>
                            <Typography
                                variant="caption" color="text.secondary"
                                sx={{ display: 'block', mb: 1 }}
                            >
                                Pick any that apply — optional.
                            </Typography>
                            <Stack
                                direction="row" spacing={0.75} useFlexGap flexWrap="wrap"
                                sx={{ mb: 2 }}
                            >
                                {tagOptions.map((opt) => {
                                    const value = opt.value ?? opt;
                                    const label = opt.label ?? value;
                                    const on = tags.includes(value);
                                    return (
                                        <Chip
                                            key={value}
                                            label={label}
                                            size="small"
                                            color={on ? 'error' : 'default'}
                                            variant={on ? 'filled' : 'outlined'}
                                            onClick={() => setTags(
                                                on
                                                    ? tags.filter((t) => t !== value)
                                                    : [...tags, value],
                                            )}
                                        />
                                    );
                                })}
                            </Stack>
                        </>
                    ) : (
                        // Rather than a silent gap under a sentence promising
                        // tags — which is exactly what shipped.
                        <Typography
                            variant="caption" color="text.secondary"
                            sx={{ display: 'block', mb: 2 }}
                        >
                            No tag list loaded for this practice, so the note below
                            is the whole story.
                        </Typography>
                    )}

                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        autoFocus
                        label="What should it have said?"
                        placeholder="Optional, and the most useful thing here."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    {/* Undo, where the mistake is made. A mis-click used to be
                        permanent from this screen: the thumbs toggle Good off
                        again, but Bad saves and opens this, and there was no
                        way back out of it. */}
                    <Button
                        color="inherit"
                        disabled={saving}
                        onClick={async () => {
                            setTags([]);
                            setComment('');
                            await save(null, [], '');
                            setOpen(false);
                        }}
                    >
                        Remove rating
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={async () => {
                            await save('BAD', tags, comment);
                            setOpen(false);
                        }}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
